import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createPageUrl } from "@/utils";
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import {
  Users,
  Calendar,
  Image,
  Play,
  Bell,
  Heart,
  MessageSquare,
  Shield,
  Church,
  Star,
  ChevronRight,
  Trophy,
  QrCode
} from 'lucide-react';

function AdminMenuCard({ item }) {
  const Icon = item.icon;

  return (
    <Link className="mbv-admin-menu-card" to={createPageUrl(item.page)}>
      <span className="mbv-admin-menu-icon"><Icon size={24} aria-hidden="true" /></span>
      <span className="mbv-admin-menu-copy">
        <span className="mbv-admin-menu-title">{item.title}</span>
        <span className="mbv-admin-menu-description">{item.description}</span>
      </span>
      {item.badge > 0 && <span className="mbv-admin-menu-badge" aria-label={`${item.badge} pendentes`}>{item.badge}</span>}
      <ChevronRight className="mbv-admin-menu-arrow" size={22} aria-hidden="true" />
    </Link>
  );
}

export default function AdminDashboard() {
  const { data: currentUser, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['permissions', currentUser?.email],
    queryFn: async () => {
      if (!currentUser?.email) return null;
      const perms = await base44.entities.AdminPermission.filter({ user_email: currentUser.email });
      return perms[0] || null;
    },
    enabled: !!currentUser?.email
  });

  const { data: stats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const [members, events, prayers, messages] = await Promise.all([
        base44.entities.Member.filter({ status: 'Pendente' }),
        base44.entities.Event.filter({ status: 'Ativo' }),
        base44.entities.PrayerRequest.filter({ status: 'Novo' }),
        base44.entities.Message.list()
      ]);
      return {
        pendingMembers: members.length,
        activeEvents: events.length,
        newPrayers: prayers.length,
        totalMessages: messages.length
      };
    }
  });

  const isSuperAdmin = currentUser?.role === 'admin' || permissions?.is_super_admin;

  const menuItems = [
    {
      title: 'Membros',
      description: 'Gerenciar cadastros',
      icon: Users,
      page: 'AdminMembros',
      badge: stats?.pendingMembers,
      permission: 'can_manage_members'
    },
    {
      title: 'Agenda',
      description: 'Eventos e programações',
      icon: Calendar,
      page: 'AdminAgenda',
      permission: 'can_manage_events'
    },
    {
      title: 'Grupos',
      description: 'Células e ministérios',
      icon: Users,
      page: 'AdminGrupos',
      permission: 'can_manage_groups'
    },
    {
      title: 'Banners',
      description: 'Avisos da home',
      icon: Bell,
      page: 'AdminBanners',
      permission: 'can_manage_banners'
    },
    {
      title: 'Inscrições',
      description: 'Eventos e participantes',
      icon: Calendar,
      page: 'AdminInscricoes',
      permission: 'can_manage_registrations'
    },
    {
      title: 'Galeria',
      description: 'Fotos e álbuns',
      icon: Image,
      page: 'AdminGaleria',
      permission: 'can_manage_gallery'
    },
    {
      title: 'Vídeos',
      description: 'Gerenciar vídeos',
      icon: Play,
      page: 'AdminVideos',
      permission: 'can_manage_gallery'
    },
    {
      title: 'Pedidos de Oração',
      description: 'Acompanhar pedidos',
      icon: Heart,
      page: 'AdminOracoes',
      badge: stats?.newPrayers
    },
    {
      title: 'Comunicação',
      description: 'Enviar mensagens',
      icon: MessageSquare,
      page: 'AdminComunicacao',
      permission: 'can_manage_communication'
    },
    {
      title: 'Avaliações',
      description: 'Moderar avaliações',
      icon: Star,
      page: 'AdminAvaliacoes',
      permission: 'can_moderate_reviews'
    },
    {
      title: 'Igreja',
      description: 'Informações e contato',
      icon: Church,
      page: 'AdminIgreja',
      permission: 'can_manage_church_info'
    },
    {
      title: 'Cultos',
      description: 'Programação semanal',
      icon: Church,
      page: 'AdminCultos',
      permission: 'can_manage_events'
    },
    {
      title: 'Ranking MBV',
      description: 'Pontuação e check-ins',
      icon: Trophy,
      page: 'AdminRankingMBV',
      permission: 'can_manage_events'
    },
    {
      title: 'QR Code MBV',
      description: 'Check-in fixo da igreja',
      icon: QrCode,
      page: 'AdminQRCodeMBV',
      permission: 'can_manage_events'
    }
  ];

  const adminItems = [
    {
      title: 'Administradores',
      description: 'Gerenciar permissões',
      icon: Shield,
      page: 'AdminPermissoes'
    },
    {
      title: 'Líderes',
      description: 'Gerenciar lideranças',
      icon: Users,
      page: 'AdminLideres'
    }
  ];

  const canAccess = (item) => {
    if (isSuperAdmin) return true;
    if (!item.permission) return true;
    return permissions?.[item.permission];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Painel Admin" subtitle="Gerencie sua igreja" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="mbv-admin-summary">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mbv-admin-stat-card"
          >
            <div className="flex items-center gap-3">
              <div className="mbv-admin-stat-icon">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.pendingMembers || 0}</p>
                <p className="text-xs text-gray-500">Pendentes</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mbv-admin-stat-card"
          >
            <div className="flex items-center gap-3">
              <div className="mbv-admin-stat-icon">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.newPrayers || 0}</p>
                <p className="text-xs text-gray-500">Orações</p>
              </div>
            </div>
          </motion.div>
        </div>

        <h2 className="mbv-admin-section-title">Gerenciar</h2>
        <div className="mbv-admin-menu-grid">
          {menuItems.filter(canAccess).map((item, index) => (
            <motion.div
              key={item.page}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <AdminMenuCard item={item} />
            </motion.div>
          ))}
        </div>

        {/* Super Admin Items */}
        {isSuperAdmin && (
          <>
            <h2 className="mbv-admin-section-title mt-8">Super Admin</h2>
            <div className="mbv-admin-menu-grid">
              {adminItems.map((item, index) => (
                <motion.div
                  key={item.page}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (menuItems.length + index) * 0.05 }}
                >
                  <AdminMenuCard item={item} />
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
